import { useMemo } from 'react'
import { FlightsClassTypes } from 'constants/flight'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { useAppSelector } from 'hooks/reduxHooks'
import { isCharterCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import { isCharterEnabled } from 'selectors/featuresSelectors'
import { getFlashCancellationPolicyView } from 'components/Common/CancellationPolicies/Flash/getFlashCancellationPolicyView'
import { getOfferCancellationPolicySelection } from 'components/Common/CancellationPolicies/Flash/getOfferCancellationPolicySelection'

interface UseOfferFlashCancellationPolicyViewParams {
  offer: App.HotelOffer | App.Offer
  checkIn?: string
  rooms: Array<App.Occupants>
  fareClass?: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  availableForPurchase?: boolean
  cheapestSelectedOption?: App.OfferPackageOptionView
}

export function useOfferFlashCancellationPolicyView(
  params: UseOfferFlashCancellationPolicyViewParams,
) {
  const {
    offer,
    checkIn,
    rooms,
    fareClass = FlightsClassTypes.ECONOMY,
    charterBundleRoute,
    availableForPurchase,
    cheapestSelectedOption,
  } = params
  const isCharterModeEnabled = useAppSelector(isCharterEnabled)
  const purchaseDate = useAppSelector(state => state.checkout.cart.existingOrder?.createdAt)
  const { cancellationPolicy, charterNights } = getOfferCancellationPolicySelection(
    offer,
    cheapestSelectedOption,
  )
  const needsCharterDepartDate = (
    availableForPurchase === true &&
    !!checkIn &&
    !!charterBundleRoute &&
    isCharterModeEnabled &&
    isCharterCancellationPolicy(cancellationPolicy)
  )
  const { departDate: charterDepartDateFromFlights } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: charterNights,
    charterBundleRoute: needsCharterDepartDate ? charterBundleRoute : undefined,
    rooms,
    fareClass,
  })
  const charterDepartDate = needsCharterDepartDate ? charterDepartDateFromFlights : undefined

  const policyView = useMemo(() => getFlashCancellationPolicyView({
    cancellationPolicy,
    propertyTimezoneOffset: offer.property?.timezoneOffset,
    checkInDate: checkIn,
    charterDepartDate,
    purchaseDate,
    isCharterModeEnabled,
    availableForPurchase: availableForPurchase === true,
    numberOfDateChangesAllowed: offer.numberOfDateChangesAllowed,
  }), [
    availableForPurchase,
    cancellationPolicy,
    charterDepartDate,
    checkIn,
    isCharterModeEnabled,
    offer.numberOfDateChangesAllowed,
    offer.property?.timezoneOffset,
    purchaseDate,
  ])

  return { policyView }
}
